import React, { createContext, useContext, useEffect, useState } from 'react';
// Firebase 핵심만 import - 성능 최적화
import { auth } from '../../firebase-core';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { deleteUserAccount, getUserFromFirestore } from '../api/firebase';

// 인증 컨텍스트 생성
export const AuthContext = createContext(null);

// 인증 컨텍스트 사용을 위한 훅
export const useAuth = () => useContext(AuthContext);

// 인증 컨텍스트 제공자
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletionState, setDeletionState] = useState({
    status: 'inactive', // 'inactive', 'deleting', 'success', 'error'
    message: ''
  });

  // 사용자 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.isAnonymous) {
        // Firestore에서 사용자 데이터를 가져옵니다.
        const userDoc = await getUserFromFirestore(user.uid);
        const userData = userDoc.success ? userDoc.data : {};

        // user 객체의 프로토타입을 유지하면서 새로운 객체를 생성합니다.
        const fullUser = Object.create(user);
        // Firestore 데이터를 별도의 속성에 저장합니다.
        fullUser.firestoreData = userData;
        
        setCurrentUser(fullUser);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // 로그아웃 함수
  const logout = async () => {
    try {
      // 🔒 보안: 로그아웃 시 모든 캐시 데이터 완전 삭제
      localStorage.clear();
      
      await signOut(auth);
  
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // 계정 삭제 시작 함수
  const startAccountDeletion = async (password) => {
    setDeletionState({ status: 'deleting', message: '' });
    
    try {
      const result = await deleteUserAccount(password);

      if (result.success) {
        setDeletionState({ status: 'success', message: '계정이 안전하게 삭제되었습니다.' });
        // 3초 후 상태 초기화 (오버레이 사라짐)
        setTimeout(() => {
          setDeletionState({ status: 'inactive', message: '' });
        }, 3000);
      } else {
        setDeletionState({ status: 'error', message: result.error || '계정 삭제에 실패했습니다.' });
      }
    } catch (error) {
      setDeletionState({ status: 'error', message: error.message || '알 수 없는 오류가 발생했습니다.' });
    }
  };

  // 제공할 값
  const value = {
    currentUser,
    isAuthenticated: !!currentUser && !loading,
    isAnonymous: currentUser?.isAnonymous || false,
    isGuest: !currentUser || loading,
    loading,
    logout,
    deletionState,
    startAccountDeletion,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 